import { queryOptions, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  staffConfigModeQueryKeyPart,
  staffConfigModeQueryParams,
} from '~/hooks/analyticsStatAccess/statAccessQueryScope';
import type { Genie2ReportingMode } from '~/utils/analyticsReportingMode';
import { customFetch } from '~/utils/customFetch';
import {
  parseStaffReportTemplatesList,
  staffReportTemplateSchema,
} from './staffReportTemplateSchema';

export type StaffReportTemplate = z.infer<typeof staffReportTemplateSchema>;
export type StaffReportTemplatesResponse = { templates: StaffReportTemplate[] };

const fetchStaffReportTemplates = async (
  statKey?: string,
  analyticsReportingMode?: Genie2ReportingMode | null,
): Promise<StaffReportTemplatesResponse> => {
  const { data } = await customFetch.get<unknown>(
    '/authenticated/staff/analytics/report-templates',
    {
      headers: { 'Content-Type': 'application/json' },
      useNodeEndpoint: true,
      params: staffConfigModeQueryParams(analyticsReportingMode, statKey ? { statKey } : undefined),
    },
  );
  return { templates: parseStaffReportTemplatesList(data) };
};

export const getStaffReportTemplatesQueryOptions = (
  statKey?: string,
  analyticsReportingMode?: Genie2ReportingMode | null,
) =>
  queryOptions({
    queryKey: [
      'staff',
      'report-templates',
      statKey,
      staffConfigModeQueryKeyPart(analyticsReportingMode),
    ],
    queryFn: () => fetchStaffReportTemplates(statKey, analyticsReportingMode),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

export const useStaffReportTemplatesQuery = (
  statKey?: string,
  analyticsReportingMode?: Genie2ReportingMode | null,
) =>
  useQuery({
    ...getStaffReportTemplatesQueryOptions(statKey, analyticsReportingMode),
  });
